import { apiClient } from './client';

/**
 * 사내 방송 API (migration 174 · 시안 화면 K + 대시보드 위젯).
 *
 * backend: apps/chat-server/src/modules/broadcast/broadcast.routes.ts
 *
 * ★★ 파일은 **브라우저가 MinIO 에 직접** 올린다 — 서버에 서명을 받고(`presignUpload`),
 *    그 주소로 PUT 한 다음, 「이 키에 이만큼 올렸다」는 줄만 만든다(`createVideo`).
 *    20MB 를 서버 본문으로 보내면 그 요청이 도는 동안 워커 하나가 통째로 묶인다.
 *
 * ★ 재생 주소는 **그때그때 받는다**(`getPlayUrl`) — 서명한 주소를 담아 두면 만료된 주소가 남는다.
 */

export type VideoSource = 'FILE' | 'URL';
export type WidgetMode = 'LATEST' | 'PLAYLIST' | 'PICKED';

export interface BroadcastChannelDto {
  id: string;
  name: string;
  isWidget: boolean;
  widgetMode: WidgetMode;
  autoplay: boolean;
  resume: boolean;
  allowedHosts: string[];
  usedBytes: number;
  quotaBytes: number;
  updatedAt: string;
  videoCount?: number;
}

export interface BroadcastVideoDto {
  id: string;
  channelId: string;
  source: VideoSource;
  title: string;
  url: string | null;
  sizeBytes: number | null;
  /** 모르면 null 이다 — 0 이 아니다. */
  durationSec: number | null;
  thumbUrl: string | null;
  /** 파일일 때만. 길이를 모르면 null. */
  quality: { mbps: number; label: string } | null;
  startsAt: string;
  endsAt: string;
  sortOrder: number;
  viewCount: number;
  /** 위젯 응답에만 — 이어보기 지점. */
  positionSec?: number;
}

/* ── 사용자 ─────────────────────────────────────────────────────── */

export async function getBroadcastWidget(): Promise<{ channel: BroadcastChannelDto | null; videos: BroadcastVideoDto[] }> {
  const { data } = await apiClient.get('/broadcast/widget');
  return data;
}

export async function getPlayUrl(videoId: string): Promise<{ url: string; kind: VideoSource }> {
  const { data } = await apiClient.get(`/broadcast/videos/${videoId}/play`);
  return data;
}

export async function saveProgress(videoId: string, positionSec: number): Promise<void> {
  await apiClient.put(`/broadcast/videos/${videoId}/progress`, { positionSec });
}

/* ── 관리 ───────────────────────────────────────────────────────── */

export async function listChannels(): Promise<BroadcastChannelDto[]> {
  const { data } = await apiClient.get('/admin/broadcast/channels');
  return data.channels ?? [];
}

export async function createChannel(name: string, isWidget = false): Promise<BroadcastChannelDto> {
  const { data } = await apiClient.post('/admin/broadcast/channels', { name, isWidget });
  return data.channel;
}

export async function updateChannel(
  id: string,
  patch: Partial<Pick<BroadcastChannelDto, 'name' | 'isWidget' | 'widgetMode' | 'autoplay' | 'resume' | 'allowedHosts'>>,
): Promise<BroadcastChannelDto> {
  const { data } = await apiClient.patch(`/admin/broadcast/channels/${id}`, patch);
  return data.channel;
}

export async function listChannelVideos(channelId: string): Promise<BroadcastVideoDto[]> {
  const { data } = await apiClient.get(`/admin/broadcast/channels/${channelId}/videos`);
  return data.videos ?? [];
}

export interface VideoWriteInput {
  source: VideoSource;
  title: string;
  fileKey?: string;
  sizeBytes?: number;
  contentType?: string;
  url?: string;
  durationSec?: number | null;
  thumbUrl?: string | null;
  startsAt: string;
  endsAt: string;
}

export async function createVideo(channelId: string, input: VideoWriteInput): Promise<BroadcastVideoDto> {
  const { data } = await apiClient.post('/admin/broadcast/videos', { channelId, ...input });
  return data.video;
}

export async function updateVideo(id: string, patch: Partial<VideoWriteInput> & { sortOrder?: number }): Promise<BroadcastVideoDto> {
  const { data } = await apiClient.patch(`/admin/broadcast/videos/${id}`, patch);
  return data.video;
}

export async function deleteVideo(id: string): Promise<void> {
  await apiClient.delete(`/admin/broadcast/videos/${id}`);
}

async function presignUpload(contentType: string, sizeBytes: number): Promise<{ uploadUrl: string; fileKey: string }> {
  const { data } = await apiClient.post('/admin/broadcast/upload-url', { contentType, sizeBytes });
  return data;
}

/**
 * 파일을 MinIO 에 직접 올린다.
 *
 * ★ `apiClient` 를 쓰지 않는다 — 이 PUT 은 **우리 서버가 아니라 저장소로** 간다.
 *   apiClient 의 인터셉터(쿠키·재발급)가 붙으면 안 되고, 붙어도 소용이 없다.
 */
export async function uploadVideoFile(file: File): Promise<{ fileKey: string; sizeBytes: number }> {
  const { uploadUrl, fileKey } = await presignUpload(file.type || 'video/mp4', file.size);
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'video/mp4' },
  });
  if (!res.ok) throw new Error(`upload_failed_${res.status}`);
  return { fileKey, sizeBytes: file.size };
}

/**
 * 고른 영상의 **길이**를 브라우저가 잰다.
 *
 * ★ 서버는 영상을 해석하지 않는다(해석하면 인코더가 서버로 들어온다). 길이는 화질 판정의
 *   재료라 여기서 재서 함께 보낸다 — **못 재면 «모른다»(null)로 둔다. 0 으로 적지 않는다.**
 */
export function readVideoDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    const done = (out: number | null) => { URL.revokeObjectURL(url); resolve(out); };
    v.onloadedmetadata = () => done(Number.isFinite(v.duration) && v.duration > 0 ? Math.round(v.duration) : null);
    v.onerror = () => done(null);
    v.src = url;
  });
}
